import { inject, injectable } from 'inversify'
import { Group, type Line } from 'three'
import { setup } from 'xstate'
import type { AuxiliaryMouseHelper } from '@/plugins/cad/module/CADModule/auxiliaryTool/auxiliaryMouseHelper'
import {
  type MouseTipHelper,
  MouseTipId,
} from '@/plugins/cad/module/CADModule/auxiliaryTool/MouseTipHelper'
import type { CADController } from '@/plugins/cad/module/CADModule/CADController'
import { CopyEntityCommand } from '@/plugins/cad/module/CADModule/command/CopyEntityCommand'
import type { CADViewManager } from '@/plugins/cad/module/CADModule/manager/CADViewManager'
import type { HistoryManager } from '@/plugins/cad/module/CADModule/manager/HistoryManager'
import type { IEntity } from '@/plugins/cad/module/CADModule/model/entity'
import type { EntitySelector } from '@/plugins/cad/module/CADModule/selector/entitySelector'
import type { IPoint2 } from '@/plugins/cad/module/common/interface'
import { Setting } from '@/plugins/cad/module/common/setting'
import { TYPES } from '@/plugins/cad/module/container/types'
import type {
  KeyEventInfoHandler,
  KeyEventParams,
  MouseEventInfoHandler,
  MouseEventParams,
} from '@/plugins/cad/module/core/common/event'
import type { SelectorEventHandler } from '@/plugins/cad/module/core/selector/Selector'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import { KeyCodes } from '@/shared/constants'

const copyEntityMachine = setup({
  actions: {
    entrySelectEntity: () => {},
    exitSelectEntity: () => {},
    entrySpecifyBasePoint: () => {},
    entrySpecifySecondPoint: () => {},
    entryCanceling: () => {},
  },
  guards: {
    isSelectedEntity: () => false,
  },
}).createMachine({
  id: 'copyEntity',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEXT: [
          {
            target: 'specifyBasePoint',
            guard: 'isSelectedEntity',
          },
          'selectEntity',
        ],
      },
    },
    selectEntity: {
      entry: { type: 'entrySelectEntity' },
      exit: { type: 'exitSelectEntity' },
      on: {
        NEXT: 'specifyBasePoint',
        MOUSE_RIGHT_DOWN: [
          {
            target: 'specifyBasePoint',
            guard: 'isSelectedEntity',
          },
          'canceling',
        ],
      },
    },
    specifyBasePoint: {
      entry: { type: 'entrySpecifyBasePoint' },
      on: {
        NEXT: 'specifySecondPoint',
      },
    },
    specifySecondPoint: {
      entry: { type: 'entrySpecifySecondPoint' },
    },
    canceling: {
      entry: { type: 'entryCanceling' },
    },
  },
})

/**
 * 复制实体工具：选中实体后指定基点与第二点，生成平移后的副本。
 */
@injectable()
export class CopyEntityTool
  extends StateMachineTool<typeof copyEntityMachine>
  implements MouseEventInfoHandler, KeyEventInfoHandler, SelectorEventHandler<IEntity>
{
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.CopyEntityTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  protected machine = copyEntityMachine.provide({
    actions: {
      entrySelectEntity: () => this.entrySelectEntity(),
      exitSelectEntity: () => this.exitSelectEntity(),
      entrySpecifyBasePoint: () => this.entrySpecifyBasePoint(),
      entrySpecifySecondPoint: () => this.entrySpecifySecondPoint(),
      entryCanceling: () => {
        this.exit()
      },
    },
    guards: {
      isSelectedEntity: () => this.isSelectedEntity(),
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private selectedEntities: IEntity[] = []
  private basePoint?: IPoint2 | undefined
  private secondPoint?: IPoint2 | undefined
  private tempPoint?: IPoint2 | undefined
  private group: Group = new Group()
  private leadLineObject?: Line | undefined
  private entityGroup?: Group | undefined

  protected onInitialize(): void {
    this.selectedEntities = this.entitySelector.getSelected()
    this.viewManager.attachTempObject(this.group)
    this.historyManager.setLock(true)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.leadLineObject && DrawUtils.disposeObject(this.leadLineObject)
    this.entityGroup && DrawUtils.disposeObject(this.entityGroup)
    this.group.parent?.remove(this.group)
    this.entitySelector.clearSelection()
    this.mouseTipHelper.removeTip(MouseTipId.Edit)
    this.historyManager.setLock(false)
    this.eject(this.auxiliaryMouseHelper)

    this.selectedEntities = []
    this.basePoint = undefined
    this.secondPoint = undefined
    this.leadLineObject = undefined
    this.entityGroup = undefined
  }

  /**
   * 鼠标按下：左键指定基点或第二点，右键在选择阶段结束实体选择。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      // 左键
      if (this.checkState('specifyBasePoint')) {
        // 指定基点
        this.basePoint = { ...params.worldPos }
        this.send({ type: 'NEXT' })
        return
      }
    } else if (params.event.button === 2) {
      // 右键
      if (this.checkState('selectEntity')) {
        this.send({ type: 'MOUSE_RIGHT_DOWN' })
        return
      }
    }

    if (this.checkState('specifySecondPoint')) {
      // 指定第二点，执行复制
      this.executeOperate()
      this.exit()
    }
  }

  /**
   * 鼠标移动：基点/第二点阶段更新预览位置。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    if (this.checkState('specifyBasePoint') || this.checkState('specifySecondPoint')) {
      this.mouseCapture(params)
      this.update()
    }
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  /**
   * 选择变化回调：记录待复制实体并更新待复制数量提示。
   *
   * @param entities - 当前已选中的实体
   */
  onSelectorChanged(entities: IEntity[]) {
    this.selectedEntities = entities
    this.mouseTipHelper.updateTip(MouseTipId.Edit, CadTipKey.edit.copy.pendingCount, {
      count: this.selectedEntities.length,
    })
  }

  private mouseCapture(params: MouseEventParams) {
    const startPoint =
      this.checkState('specifySecondPoint') && this.basePoint ? this.basePoint : undefined

    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params, { startPoint })

    this.tempPoint = { x: finalPoint.x, y: finalPoint.y }

    if (this.checkState('specifySecondPoint')) {
      this.secondPoint = { ...this.tempPoint }
    }
  }

  /************ 状态机状态处理 ************/
  private isSelectedEntity() {
    return this.selectedEntities.length > 0
  }

  private entrySelectEntity() {
    this.inject(this.entitySelector)
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.copy.pendingCount, {
      count: this.selectedEntities.length,
    })
  }

  private exitSelectEntity() {
    this.eject(this.entitySelector)
  }

  private entrySpecifyBasePoint() {
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.copy.basePoint)
    this.inject(this.auxiliaryMouseHelper)
  }

  private entrySpecifySecondPoint() {
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.copy.targetPoint)
  }

  /************ 渲染 ************/
  private update() {
    if (!this.basePoint || !this.secondPoint) {
      return
    }

    const line = { startPoint: this.basePoint!, endPoint: this.secondPoint! }
    const diffX = this.secondPoint!.x - this.basePoint!.x
    const diffY = this.secondPoint!.y - this.basePoint!.y
    this.updateLeadLine(line)
    this.updateEntityGroup({ x: diffX, y: diffY })
  }

  // 根据selectedEntities创建一个THREE.Group用于显示移动效果
  private updateEntityGroup(translate: { x: number; y: number }) {
    if (!this.entityGroup) {
      this.entityGroup = new Group()
      this.group.add(this.entityGroup)

      this.selectedEntities.forEach((entity) => {
        const object = this.viewManager.getEntityObject(entity)
        const cloneObject = object?.clone()
        cloneObject && this.entityGroup!.add(cloneObject)
      })
    }
    this.entityGroup.position.set(translate.x, translate.y, 0)
  }

  // 更新引导线
  private updateLeadLine(line: { startPoint: IPoint2; endPoint: IPoint2 }) {
    if (!this.leadLineObject) {
      const dashSize = this.controller.getPixelSizeInWorld(Setting.dashSize)
      const materialOprion = {
        color: Setting.leadLineConfig.color,
        opacity: Setting.leadLineConfig.opacity,
        transparent: Setting.leadLineConfig.opacity < 1,
        dashSize,
        gapSize: dashSize / 2,
        depthTest: false,
        depthWrite: false,
      }
      this.leadLineObject = DrawUtils.drawDashedLine(
        line.startPoint,
        line.endPoint,
        materialOprion,
        this.group
      )
    } else {
      const geometry = DrawUtils.getBufferGeometryByPoints([line.startPoint, line.endPoint])
      this.leadLineObject.geometry = geometry
      this.leadLineObject.geometry.attributes.position.needsUpdate = true
      this.leadLineObject.computeLineDistances()
    }
  }

  /************ 图元数据处理 ************/
  private executeOperate() {
    if (!this.basePoint || !this.secondPoint) {
      return
    }
    const translate = {
      x: this.secondPoint.x - this.basePoint.x,
      y: this.secondPoint.y - this.basePoint.y,
    }
    this.historyManager.addCommandAndExecute(
      new CopyEntityCommand(this.selectedEntities, { translate }, this.viewManager)
    )
  }
}
